import React, { useEffect, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { FiCheckCircle, FiX, FiPlus } from "react-icons/fi";
import {
  getSubscriptionPlanDetails,
  updateSubscriptionPlan,
} from "../../redux/services/SubscriptionServices/Subscription.Services";
import { successToast, errorToast } from "../../Utils/toast";
import { ActiveModuleList } from "../../redux/services/moduleService/module.Services";
import { ActiveCategoryList } from "../../redux/services/categoriesService/categories.Services";

const INITIAL_FORM = {
  name: "",
  description: "",
  module_id: "",
  // category_id: "",
  price: "",
  original_price: "",
  duration_days: 30,
  ai_call_limit: 0,
  // staff_limit: 0,
  // product_limit: 0,
  // features: [""],
  // is_popular: 0,
  // badge_text: "",
  // sort_order: 0,
  status: 1,
};

function EditPlan() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [form, setForm] = useState(INITIAL_FORM);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(true);
  const [modules, setModules] = useState([]);
  const [categories, setCategories] = useState([]);

  useEffect(() => {
    const fetchAll = async () => {
      if (!id) {
        navigate("/plan-list");
        return;
      }
      try {
        setFetching(true);

        const [modulesRes, categoriesRes, planRes] = await Promise.all([
          ActiveModuleList(),
          ActiveCategoryList(),
          getSubscriptionPlanDetails(id),
        ]);

        setModules(modulesRes?.data || []);
        setCategories(categoriesRes?.data || []);

        console.log("PLAN RESPONSE:", planRes);

        // Response shape: { success, message, data: {...plan...} }
        let data = null;
        if (planRes?.success && planRes?.data) {
          data = planRes.data;
        } else if (planRes?.data?.data) {
          data = planRes.data.data;
        } else if (planRes?.id) {
          data = planRes;
        }

        if (!data) {
          errorToast("Plan not found");
          navigate("/plan-list");
          return;
        }

        setForm({
          ...INITIAL_FORM,
          name: data.name ?? "",
          description: data.description ?? "",
          module_id: data.module_id ?? "",
          category_id: data.category_id ?? "",
          price: data.price ?? "",
          original_price: data.original_price ?? "",
          // duration_days: data.duration_days ?? 30,
          ai_call_limit: data.ai_call_limit ?? 0,
          // staff_limit: data.staff_limit ?? 0,
          // product_limit: data.product_limit ?? 0,
          // features:
          //   Array.isArray(data.features) && data.features.length > 0
          //     ? data.features
          //     : [""],
          // is_popular: Number(data.is_popular) ? 1 : 0,
          // badge_text: data.badge_text ?? "",
          // sort_order: data.sort_order ?? 0,
          status: data.status ?? 1,
        });
      } catch (error) {
        console.error(error);
        errorToast("Failed to load plan");
      } finally {
        setFetching(false);
      }
    };

    fetchAll();
    // eslint-disable-next-line
  }, [id]);

  const handleChange = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) setErrors((prev) => ({ ...prev, [field]: "" }));
  };

  // ---- Features handlers (kept for future use; safe guards) ----
  const handleFeatureChange = (index, value) => {
    setForm((prev) => {
      const features = Array.isArray(prev.features) ? [...prev.features] : [""];
      features[index] = value;
      return { ...prev, features };
    });
  };

  const addFeature = () =>
    setForm((prev) => ({
      ...prev,
      features: Array.isArray(prev.features) ? [...prev.features, ""] : [""],
    }));

  const removeFeature = (index) =>
    setForm((prev) => {
      const features = Array.isArray(prev.features)
        ? prev.features.filter((_, i) => i !== index)
        : [""];
      return { ...prev, features: features.length ? features : [""] };
    });

  const validate = () => {
    const err = {};
    if (!form.name?.trim()) err.name = "Plan name is required";
    if (!form.module_id) err.module_id = "Module is required";
    // if (!form.category_id) err.category_id = "Category is required";
    if (form.price === "" || isNaN(Number(form.price)))
      err.price = "Valid price is required";
    if (
      form.original_price !== "" &&
      form.original_price !== null &&
      Number(form.original_price) < Number(form.price)
    )
      err.original_price = "Original price must be >= price";
    if (!form.duration_days || Number(form.duration_days) < 1)
      err.duration_days = "Duration must be at least 1 day";
    setErrors(err);
    return Object.keys(err).length === 0;
  };

  // Helper to extract error message from various API shapes
  const extractErrorMessage = (res, fallback) => {
    if (!res) return fallback;
    if (typeof res === "string") return res;
    if (res.message) return res.message;
    if (res.error) return res.error;
    if (res.errors) {
      // Object of field: [msg]
      if (typeof res.errors === "object") {
        const firstKey = Object.keys(res.errors)[0];
        const val = res.errors[firstKey];
        return Array.isArray(val) ? val[0] : val;
      }
      return res.errors;
    }
    if (res.data?.message) return res.data.message;
    if (res.data?.errors) {
      const errs = res.data.errors;
      if (typeof errs === "object") {
        const firstKey = Object.keys(errs)[0];
        const val = errs[firstKey];
        return Array.isArray(val) ? val[0] : val;
      }
      return errs;
    }
    return fallback;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;

    try {
      setLoading(true);

      // Build features only if you re-enable them
      const cleanedFeatures = Array.isArray(form.features)
        ? form.features.map((f) => f.trim()).filter(Boolean)
        : [];

      // Build payload matching your API contract
      const payload = {
        id: Number(id),
        name: form.name.trim(),
        description: form.description?.trim() || "",
        module_id: Number(form.module_id),
        // category_id: Number(form.category_id),
        price: Number(form.price),
        // IMPORTANT: send null instead of 0 when empty (most APIs expect this)
        original_price:
          form.original_price !== "" &&
          form.original_price !== null &&
          form.original_price !== undefined
            ? Number(form.original_price)
            : null,
        // duration_days: Number(form.duration_days),
        ai_call_limit: Number(form.ai_call_limit) || 0,
        // staff_limit: Number(form.staff_limit) || 0,
        // product_limit: Number(form.product_limit) || 0,
        // features: cleanedFeatures,
        // is_popular: Number(form.is_popular) || 0,
        // badge_text: form.badge_text?.trim() || null,
        // sort_order: Number(form.sort_order) || 0,
        status: Number(form.status),
      };

      console.log("UPDATE PAYLOAD:", payload);

      const res = await updateSubscriptionPlan(payload);
      console.log("UPDATE RESPONSE:", res);

      // Broader success detection:
      // Treat as failure ONLY if success === false, or status === false,
      // or an explicit error field exists.
      const isFailure =
        res?.success === false ||
        res?.status === false ||
        res?.error ||
        (res?.data && res?.data?.success === false);

      if (!isFailure) {
        successToast(
          res?.message || res?.data?.message || "Plan updated successfully"
        );
        navigate("/plan-list");
      } else {
        errorToast(
          extractErrorMessage(res, "Failed to update plan")
        );
      }
    } catch (error) {
      console.error("UPDATE ERROR:", error);
      // Log the full server response to inspect validation errors
      console.error("SERVER RESPONSE:", error?.response?.data);
      const msg =
        error?.response?.data?.message ||
        error?.response?.data?.error ||
        (error?.response?.data?.errors
          ? extractErrorMessage(error.response.data, "")
          : "") ||
        error?.message ||
        "Failed to update plan";
      errorToast(msg);
    } finally {
      setLoading(false);
    }
  };

  if (fetching) {
    return (
      <div className="text-center py-5">
        <div className="spinner-border text-primary"></div>
        <p className="mt-2">Loading plan details...</p>
      </div>
    );
  }

  return (
    <>
      <div className="d-flex align-items-center justify-content-between page-header-breadcrumb">
        <div className="title_member">
          <h4>Edit Subscription Plan</h4>
          <nav className="page-breadcrumb">
            <Link to="/">Home</Link>
            <span className="mx-2">/</span>
            <Link to="/plan-list">Subscriptions</Link>
            <span className="mx-2">/</span>
            <span>Edit</span>
          </nav>
        </div>
        <div className="d-flex flex-wrap gap-2 justify-content-end">
          <button className="add-btn" onClick={() => navigate(-1)}>
            Back
          </button>
        </div>
      </div>

      <div className="main_page">
        <div className="card mt-2">
          <div className="card-body">
            <form onSubmit={handleSubmit} className="row g-3">
              {/* PLAN NAME */}
              <div className="col-md-6">
                <label className="form-label fw-bold">
                  Plan Name <span className="text-danger">*</span>
                </label>
                <input
                  type="text"
                  className={`form-control ${errors.name ? "is-invalid" : ""}`}
                  value={form.name}
                  onChange={(e) => handleChange("name", e.target.value)}
                />
                {errors.name && (
                  <div className="invalid-feedback">{errors.name}</div>
                )}
              </div>

              {/* MODULE */}
              <div className="col-md-6">
                <label className="form-label fw-bold">
                  Module <span className="text-danger">*</span>
                </label>
                <select
                  className={`form-control form-select ${
                    errors.module_id ? "is-invalid" : ""
                  }`}
                  value={form.module_id}
                  onChange={(e) => handleChange("module_id", e.target.value)}
                >
                  <option value="">Select Module</option>
                  {modules.map((mod) => (
                    <option key={mod.id} value={mod.id}>
                      {mod.name}
                    </option>
                  ))}
                </select>
                {errors.module_id && (
                  <div className="invalid-feedback">{errors.module_id}</div>
                )}
              </div>

              {/* CATEGORY (commented as before) */}

              {/* AI CALL LIMIT */}
              <div className="col-md-4">
                <label className="form-label fw-bold">AI Call Limit</label>
                <input
                  type="number"
                  className="form-control"
                  value={form.ai_call_limit}
                  onChange={(e) =>
                    handleChange("ai_call_limit", e.target.value)
                  }
                />
              </div>

              {/* PRICE */}
              <div className="col-md-4">
                <label className="form-label fw-bold">
                  Price (₹) <span className="text-danger">*</span>
                </label>
                <input
                  type="number"
                  step="0.01"
                  className={`form-control ${errors.price ? "is-invalid" : ""}`}
                  value={form.price}
                  onChange={(e) => handleChange("price", e.target.value)}
                />
                {errors.price && (
                  <div className="invalid-feedback">{errors.price}</div>
                )}
              </div>

              {/* ORIGINAL PRICE */}
              <div className="col-md-4">
                <label className="form-label fw-bold">Original Price (₹)</label>
                <input
                  type="number"
                  step="0.01"
                  className={`form-control ${
                    errors.original_price ? "is-invalid" : ""
                  }`}
                  value={form.original_price}
                  onChange={(e) =>
                    handleChange("original_price", e.target.value)
                  }
                />
                {errors.original_price && (
                  <div className="invalid-feedback">
                    {errors.original_price}
                  </div>
                )}
              </div>

              {/* STATUS */}
              <div className="col-md-6">
                <label className="form-label fw-bold">Status</label>
                <select
                  className="form-select form-control"
                  value={form.status}
                  onChange={(e) => handleChange("status", e.target.value)}
                >
                  <option value={1}>Active</option>
                  <option value={0}>Inactive</option>
                </select>
              </div>

              {/* DESCRIPTION */}
              <div className="col-md-12">
                <label className="form-label fw-bold">Description</label>
                <textarea
                  rows={2}
                  className="form-control"
                  value={form.description}
                  onChange={(e) => handleChange("description", e.target.value)}
                />
              </div>

              {/* DURATION (commented as before) */}

              {/* SUBMIT */}
              <div className="col-12 text-end pt-3 mt-3">
                <button
                  type="button"
                  className="btn btn-danger me-2"
                  onClick={() => navigate(-1)}
                  disabled={loading}
                >
                  Cancel
                </button>
                <button type="submit" className="add-btn" disabled={loading}>
                  {loading ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2"></span>
                      Updating...
                    </>
                  ) : (
                    "Update Plan"
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </>
  );
}

export default EditPlan;